import type { Meta } from '@cubejs-client/core'
import type { Editor, JSONContent } from '@tiptap/react'

import { listMembers, listViews } from '@/components/member-picker/members'
import { repeatOverAt } from '@/features/documents/nodes/resolve'
import type { messages } from '@/lib/messages'

import type { DocumentTemplate } from './serialize'

export const INSERT_KINDS = [
  'reportKpi',
  'reportTable',
  'reportList',
  'reportChart',
  'value',
  'change',
  'condition',
  'repeat',
  'pagebreak',
  'note',
] as const

export type InsertKind = (typeof INSERT_KINDS)[number]

/** Ekle menüsünde kapalı öğenin gerekçesi (`messages.documents.editor.insert` anahtarı). */
export type InsertReason = keyof typeof messages.documents.editor.insert

export type InsertBuild = { ok: true; node: JSONContent } | { ok: false; reason: InsertReason }

const EMPTY_PARAGRAPH = [{ type: 'paragraph' }]

type WidgetKind = 'reportKpi' | 'reportTable' | 'reportList' | 'reportChart'

/**
 * Widget'ın verilen view için geçerli başlangıç öznitelikleri (şemadan geçer); view'da gereken
 * üye yoksa `undefined`. View değişince ayar paneli de üyeleri bununla sıfırlar.
 */
export function widgetAttrs(
  kind: WidgetKind,
  meta: Meta,
  view: string,
): Record<string, unknown> | undefined {
  const measure = listMembers(meta, view, 'measure')[0]?.value
  const dimension = listMembers(meta, view, 'dimension')[0]?.value
  const time = listMembers(meta, view, 'time')[0]?.value
  switch (kind) {
    case 'reportKpi':
      return measure ? { view, measure } : undefined
    case 'reportTable':
      return measure
        ? { view, measures: [measure], dimensions: dimension ? [dimension] : [] }
        : undefined
    case 'reportList':
      return dimension ? { view, columns: [dimension], rowKey: dimension } : undefined
    case 'reportChart':
      return measure && time ? { view, time, series: [{ measure, type: 'bar' }] } : undefined
  }
}

/** Şablon filtrelerinin view'ları önce gelir (belge aynı veri üzerinde çalışır). */
function widgetNode(
  kind: WidgetKind,
  meta: Meta | undefined,
  template: DocumentTemplate,
): InsertBuild {
  if (!meta) return { ok: false, reason: 'needMeta' }
  const views = listViews(meta).map((option) => option.value)
  if (views.length === 0) return { ok: false, reason: 'needMeta' }
  const order = [...new Set([...template.filters.map((filter) => filter.view), ...views])]
  for (const view of order.filter((name) => views.includes(name))) {
    const attrs = widgetAttrs(kind, meta, view)
    if (attrs) return { ok: true, node: { type: kind, attrs } }
  }
  return { ok: false, reason: 'needMember' }
}

/** Menü öğesinin ekleyeceği düğüm (varsayılanları şemadan geçer) ya da kapalı olma gerekçesi. */
export function buildInsert(
  kind: InsertKind,
  meta: Meta | undefined,
  template: DocumentTemplate,
): InsertBuild {
  const queries = Object.entries(template.queries)
  switch (kind) {
    case 'reportKpi':
    case 'reportTable':
    case 'reportList':
    case 'reportChart':
      return widgetNode(kind, meta, template)
    case 'value': {
      const query = queries[0]
      if (!query) return { ok: false, reason: 'needQuery' }
      const of = `${query[0]}.${query[1].measures[0]}`
      return { ok: true, node: { type: 'value', attrs: { of } } }
    }
    case 'change': {
      if (queries.length === 0) return { ok: false, reason: 'needQuery' }
      const query = queries.find(([, def]) => def.compare === 'previous')
      if (!query) return { ok: false, reason: 'needCompare' }
      const of = `${query[0]}.${query[1].measures[0]}`
      return { ok: true, node: { type: 'change', attrs: { of } } }
    }
    case 'condition': {
      const query = queries[0]
      if (!query) return { ok: false, reason: 'needQuery' }
      return {
        ok: true,
        node: {
          type: 'condition',
          attrs: { left: `${query[0]}.${query[1].measures[0]}`, op: 'gt', right: 0 },
          content: [
            { type: 'conditionThen', content: EMPTY_PARAGRAPH },
            { type: 'conditionElse', content: EMPTY_PARAGRAPH },
          ],
        },
      }
    }
    case 'repeat': {
      if (queries.length === 0) return { ok: false, reason: 'needQuery' }
      const query = queries.find(([, def]) => def.dimensions.length > 0)
      if (!query) return { ok: false, reason: 'needRepeatQuery' }
      return {
        ok: true,
        node: { type: 'repeat', attrs: { over: query[0], limit: 50 }, content: EMPTY_PARAGRAPH },
      }
    }
    case 'pagebreak':
      return { ok: true, node: { type: 'pagebreak' } }
    case 'note':
      return { ok: true, node: { type: 'note', attrs: { type: 'info' }, content: EMPTY_PARAGRAPH } }
  }
}

/** İmleç bir `repeat` içindeyse yeni `repeat` eklenemez (şema: iç içe tekrar yok). */
export const insideRepeat = (editor: Editor) =>
  repeatOverAt(editor.state.doc, editor.state.selection.from) !== undefined

export function insertNode(
  editor: Editor,
  kind: InsertKind,
  meta: Meta | undefined,
  template: DocumentTemplate,
): boolean {
  if (kind === 'repeat' && insideRepeat(editor)) return false
  const built = buildInsert(kind, meta, template)
  return built.ok && editor.chain().focus().insertContent(built.node).run()
}
